import { useLayoutEffect, useRef, useState } from "react";

/**
 * Card carousel matching template (c-track / slides / dots / arrows).
 * Expects `photos` as Airtable-style array of { url }.
 */
export default function CardMediaCarousel({ photos = [], alt = "", onOpen }) {
  const slides = photos.filter((p) => p?.url);
  const [idx, setIdx] = useState(0);
  const wrapRef = useRef(null);
  const [slideW, setSlideW] = useState(0);
  const len = slides.length;
  const i = len ? ((idx % len) + len) % len : 0;

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const read = () => {
      const w = el.getBoundingClientRect().width;
      setSlideW(Number.isFinite(w) ? w : 0);
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!len) {
    return (
      <div className="c-slide emoji-fb" onClick={onOpen} role="presentation">
        —
      </div>
    );
  }

  const go = (dir) => {
    setIdx((prev) => (prev + dir + len) % len);
  };

  const offsetPx = slideW > 0 ? -(i * slideW) : undefined;
  const trackStyle = {
    transform:
      offsetPx != null
        ? `translate3d(${offsetPx}px,0,0)`
        : `translate3d(-${i * 100}%,0,0)`,
  };

  return (
    <div
      ref={wrapRef}
      className="c-wrap card-carousel-root"
      onClick={onOpen}
      role="presentation"
    >
      <div className="c-track" style={trackStyle}>
        {slides.map((p, j) => (
          <div key={j} className="c-slide">
            <img src={p.url} alt={alt} />
          </div>
        ))}
      </div>
      {len > 1 && (
        <>
          <button
            type="button"
            className="c-prev"
            aria-label="Previous image"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
          >
            ‹
          </button>
          <button
            type="button"
            className="c-next"
            aria-label="Next image"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
          >
            ›
          </button>
          <div className="c-dots">
            {slides.map((_, j) => (
              <button
                key={j}
                type="button"
                className={`c-dot${j === i ? " on" : ""}`}
                aria-label={`Slide ${j + 1}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setIdx(j);
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
